import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { 
  Users, 
  CreditCard, 
  Heart, 
  Download,
  FileText,
  Filter,
  X
} from 'lucide-react';
import { getApiUrl } from '../utils/apiConfig';

// Utilidades
const fechaCorta = (fecha) => {
  if (!fecha) return '-';
  const date = new Date(fecha);
  return date.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const traducirEstado = (estado) => {
  const estados = {
    'completed': 'Pagado',
    'pending': 'Pendiente',
    'overdue': 'Vencido',
    'active': 'Activo',
    'inactive': 'Inactivo',
    'pendiente': 'Pendiente',
    'expirado': 'Expirado'
  };
  return estados[estado] || estado;
};

const traducirMetodo = (metodo) => {
  if (!metodo) return '-';
  const metodos = {
    'wechat': 'WeChat Pay',
    'alipay': 'Efectivo',
    'bank': 'Transferencia Bancaria',
    'cash': 'Efectivo',
    'card': 'Tarjeta',
    'transfer': 'Transferencia Bancaria',
    'paypal': 'PayPal'
  };
  return metodos[metodo.toLowerCase()] || metodo;
};

const Reports = () => {
  const [reportType, setReportType] = useState('members');
  
  // Datos
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [donations, setDonations] = useState([]);
  
  // Filtros para Socios
  const [memberFilters, setMemberFilters] = useState({
    status: 'all',
    membershipType: 'all',
    dateFrom: '',
    dateTo: '',
    searchText: ''
  });
  
  // Filtros para Pagos
  const [paymentFilters, setPaymentFilters] = useState({
    status: 'all',
    year: 'all',
    membershipType: 'all',
    memberId: 'all',
    amountFrom: '',
    amountTo: '',
    dateFrom: '',
    dateTo: ''
  });
  
  // Estados para búsqueda de socio
  const [memberSearchText, setMemberSearchText] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  
  // Filtros para Donaciones
  const [donationFilters, setDonationFilters] = useState({
    paymentMethod: 'all',
    amountFrom: '',
    amountTo: '',
    dateFrom: '',
    dateTo: '',
    searchText: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);

  // Cargar datos
  useEffect(() => {
    fetch(getApiUrl('/api/miembros-con-ultimo-pago'))
      .then(res => res.json())
      .then(data => setMembers(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error cargando socios:', err));

    fetch(getApiUrl('/api/pagos'))
      .then(res => res.json())
      .then(data => setPayments(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error cargando pagos:', err));

    fetch(getApiUrl('/api/donaciones'))
      .then(res => res.json())
      .then(data => setDonations(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error cargando donaciones:', err));
  }, []);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.member-search-container')) {
        setShowMemberDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Aplicar filtros a socios
  const getFilteredMembers = () => {
    return members.filter(member => {
      if (memberFilters.status !== 'all' && member.status !== memberFilters.status) return false;
      if (memberFilters.membershipType !== 'all' && member.membershipType !== memberFilters.membershipType) return false;
      
      if (memberFilters.dateFrom && member.joinDate) {
        const joinDate = new Date(member.joinDate);
        const fromDate = new Date(memberFilters.dateFrom);
        if (joinDate < fromDate) return false;
      }
      
      if (memberFilters.dateTo && member.joinDate) {
        const joinDate = new Date(member.joinDate);
        const toDate = new Date(memberFilters.dateTo);
        if (joinDate > toDate) return false;
      }
      
      if (memberFilters.searchText) {
        const search = memberFilters.searchText.toLowerCase();
        const matchName = member.name?.toLowerCase().includes(search);
        const matchEmail = member.email?.toLowerCase().includes(search);
        const matchRut = member.rut?.toLowerCase().includes(search);
        if (!matchName && !matchEmail && !matchRut) return false;
      }
      
      return true;
    });
  };

  // Aplicar filtros a pagos
  const getFilteredPayments = () => {
    return payments.filter(payment => {
      if (paymentFilters.status !== 'all' && payment.status !== paymentFilters.status) return false;
      if (paymentFilters.year !== 'all' && payment.año_periodo !== parseInt(paymentFilters.year)) return false;
      if (paymentFilters.membershipType !== 'all' && payment.membershipType !== paymentFilters.membershipType) return false;
      if (paymentFilters.memberId !== 'all' && payment.memberId !== parseInt(paymentFilters.memberId)) return false;
      
      if (paymentFilters.amountFrom && parseFloat(payment.amount) < parseFloat(paymentFilters.amountFrom)) return false;
      if (paymentFilters.amountTo && parseFloat(payment.amount) > parseFloat(paymentFilters.amountTo)) return false;
      
      if (paymentFilters.dateFrom && payment.paymentDate) {
        const payDate = new Date(payment.paymentDate);
        const fromDate = new Date(paymentFilters.dateFrom);
        if (payDate < fromDate) return false;
      }
      
      if (paymentFilters.dateTo && payment.paymentDate) {
        const payDate = new Date(payment.paymentDate);
        const toDate = new Date(paymentFilters.dateTo);
        if (payDate > toDate) return false;
      }
      
      return true;
    });
  };

  // Aplicar filtros a donaciones
  const getFilteredDonations = () => {
    return donations.filter(donation => {
      if (donationFilters.paymentMethod !== 'all' && donation.paymentMethod !== donationFilters.paymentMethod) return false;
      
      if (donationFilters.amountFrom && parseFloat(donation.amount) < parseFloat(donationFilters.amountFrom)) return false;
      if (donationFilters.amountTo && parseFloat(donation.amount) > parseFloat(donationFilters.amountTo)) return false;
      
      if (donationFilters.dateFrom && donation.donationDate) {
        const donDate = new Date(donation.donationDate);
        const fromDate = new Date(donationFilters.dateFrom);
        if (donDate < fromDate) return false;
      }
      
      if (donationFilters.dateTo && donation.donationDate) {
        const donDate = new Date(donation.donationDate);
        const toDate = new Date(donationFilters.dateTo);
        if (donDate > toDate) return false;
      }
      
      if (donationFilters.searchText) {
        const search = donationFilters.searchText.toLowerCase();
        const matchDonor = donation.donorName?.toLowerCase().includes(search);
        const matchNotes = donation.notes?.toLowerCase().includes(search);
        if (!matchDonor && !matchNotes) return false;
      }
      
      return true;
    });
  };

  // Obtener años únicos de pagos
  const getUniqueYears = () => {
    const years = new Set();
    payments.forEach(p => {
      if (p.año_periodo) years.add(p.año_periodo);
    });
    return Array.from(years).sort((a, b) => b - a);
  };

  // Obtener tipos de membresía únicos
  const getUniqueMembershipTypes = () => {
    const types = new Set();
    members.forEach(m => {
      if (m.membershipType) types.add(m.membershipType);
    });
    return Array.from(types);
  };

  // Limpiar filtros
  const clearFilters = () => {
    if (reportType === 'members') {
      setMemberFilters({
        status: 'all',
        membershipType: 'all',
        dateFrom: '',
        dateTo: '',
        searchText: ''
      });
    } else if (reportType === 'payments') {
      setPaymentFilters({
        status: 'all',
        year: 'all',
        membershipType: 'all',
        memberId: 'all',
        amountFrom: '',
        amountTo: '',
        dateFrom: '',
        dateTo: ''
      });
      setMemberSearchText('');
      setShowMemberDropdown(false);
    } else if (reportType === 'donations') {
      setDonationFilters({
        paymentMethod: 'all',
        amountFrom: '',
        amountTo: '',
        dateFrom: '',
        dateTo: '',
        searchText: ''
      });
    }
  };

  // Exportar a CSV
  const exportToCSV = () => {
    let data, headers, filename;
    
    if (reportType === 'members') {
      const filtered = getFilteredMembers();
      headers = ['ID', 'Nombre', 'Email', 'RUT', 'Teléfono', 'Tipo de Membresía', 'Estado', 'Fecha de Ingreso'];
      data = filtered.map(m => [
        m.id,
        m.name,
        m.email,
        m.rut || '',
        m.phone || '',
        m.membershipType,
        traducirEstado(m.status),
        fechaCorta(m.joinDate)
      ]);
      filename = 'reporte_socios.csv';
    } else if (reportType === 'payments') {
      const filtered = getFilteredPayments();
      headers = ['ID', 'Socio', 'Monto', 'Estado', 'Fecha de Pago', 'Vencimiento', 'Tipo Membresía', 'Año/Período', 'Método'];
      data = filtered.map(p => [
        p.id,
        p.memberName,
        p.amount,
        traducirEstado(p.status),
        fechaCorta(p.paymentDate),
        fechaCorta(p.dueDate),
        p.membershipType || '',
        p.año_periodo || '',
        traducirMetodo(p.paymentMethod)
      ]);
      filename = 'reporte_pagos.csv';
    } else if (reportType === 'donations') {
      const filtered = getFilteredDonations();
      headers = ['ID', 'Donante', 'Monto', 'Fecha', 'Método', 'Notas'];
      data = filtered.map(d => [
        d.id,
        d.donorName,
        d.amount,
        fechaCorta(d.donationDate),
        traducirMetodo(d.paymentMethod),
        d.notes || ''
      ]);
      filename = 'reporte_donaciones.csv';
    }
    
    // Construir CSV
    const csvContent = [
      headers.join(','),
      ...data.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  // Exportar a PDF
  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    if (reportType === 'members') {
      const filtered = getFilteredMembers();
      
      // Título
      doc.setFontSize(18);
      doc.setTextColor(30, 64, 175);
      doc.text('Reporte de Socios', pageWidth / 2, 15, { align: 'center' });
      
      // Subtítulo con total
      doc.setFontSize(12);
      doc.setTextColor(75, 85, 99);
      doc.text(`Total de socios: ${filtered.length}`, pageWidth / 2, 23, { align: 'center' });
      
      // Tabla
      doc.autoTable({
        startY: 30,
        head: [['ID', 'Nombre', 'Email', 'RUT', 'Membresía', 'Estado', 'F. Ingreso']],
        body: filtered.map(m => [
          m.id,
          m.name,
          m.email,
          m.rut || '-',
          m.membershipType,
          traducirEstado(m.status),
          fechaCorta(m.joinDate)
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [30, 64, 175], textColor: 255 }
      });
      
      doc.save('reporte_socios.pdf');
      
    } else if (reportType === 'payments') {
      const filtered = getFilteredPayments();
      const totalPagado = filtered.filter(p => p.status === 'completed').reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      const totalPendiente = filtered.filter(p => p.status === 'pending').reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      const totalVencido = filtered.filter(p => p.status === 'overdue').reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      
      // Título
      doc.setFontSize(18);
      doc.setTextColor(16, 185, 129);
      doc.text('Reporte de Pagos de Membresía', pageWidth / 2, 15, { align: 'center' });
      
      // Resumen
      doc.setFontSize(10);
      doc.setTextColor(75, 85, 99);
      doc.text(`Total pagos: ${filtered.length}  |  Pagado: $${totalPagado.toLocaleString('es-ES')}  |  Pendiente: $${totalPendiente.toLocaleString('es-ES')}  |  Vencido: $${totalVencido.toLocaleString('es-ES')}`, pageWidth / 2, 23, { align: 'center' });
      
      // Tabla
      doc.autoTable({
        startY: 30,
        head: [['ID', 'Socio', 'Monto', 'Estado', 'F. Pago', 'Tipo', 'Año', 'Método']],
        body: filtered.map(p => [
          p.id,
          p.memberName,
          `$${parseFloat(p.amount).toLocaleString('es-ES')}`,
          traducirEstado(p.status),
          fechaCorta(p.paymentDate),
          p.membershipType || '-',
          p.año_periodo || '-',
          traducirMetodo(p.paymentMethod)
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [16, 185, 129], textColor: 255 }
      });
      
      doc.save('reporte_pagos.pdf');
      
    } else if (reportType === 'donations') {
      const filtered = getFilteredDonations();
      const totalDonado = filtered.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
      
      // Título
      doc.setFontSize(18);
      doc.setTextColor(239, 68, 68);
      doc.text('Reporte de Donaciones', pageWidth / 2, 15, { align: 'center' });
      
      // Resumen
      doc.setFontSize(10);
      doc.setTextColor(75, 85, 99);
      doc.text(`Total donaciones: ${filtered.length}  |  Monto total: $${totalDonado.toLocaleString('es-ES')}`, pageWidth / 2, 23, { align: 'center' });
      
      // Tabla
      doc.autoTable({
        startY: 30,
        head: [['ID', 'Donante', 'Monto', 'Fecha', 'Método', 'Notas']],
        body: filtered.map(d => [
          d.id,
          d.donorName,
          `$${parseFloat(d.amount).toLocaleString('es-ES')}`,
          fechaCorta(d.donationDate),
          traducirMetodo(d.paymentMethod),
          (d.notes || '').substring(0, 40)
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [239, 68, 68], textColor: 255 }
      });
      
      doc.save('reporte_donaciones.pdf');
    }
  };

  // Renderizar filtros según el tipo de reporte
  const renderFilters = () => {
    if (!showFilters) return null;
    
    if (reportType === 'members') {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-gray-50 p-6 rounded-xl space-y-4 border border-gray-200"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
              <select
                value={memberFilters.status}
                onChange={(e) => setMemberFilters({...memberFilters, status: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Todos los estados</option>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="pendiente">Pendiente</option>
                <option value="expirado">Expirado</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Membresía</label>
              <select
                value={memberFilters.membershipType}
                onChange={(e) => setMemberFilters({...memberFilters, membershipType: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Todos los tipos</option>
                {getUniqueMembershipTypes().map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar (Nombre, Email, RUT)</label>
              <input
                type="text"
                value={memberFilters.searchText}
                onChange={(e) => setMemberFilters({...memberFilters, searchText: e.target.value})}
                placeholder="Buscar..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Ingreso Desde</label>
              <input
                type="date"
                value={memberFilters.dateFrom}
                onChange={(e) => setMemberFilters({...memberFilters, dateFrom: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Ingreso Hasta</label>
              <input
                type="date"
                value={memberFilters.dateTo}
                onChange={(e) => setMemberFilters({...memberFilters, dateTo: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
          >
            <X className="w-4 h-4" />
            Limpiar Filtros
          </button>
        </motion.div>
      );
    } else if (reportType === 'payments') {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-gray-50 p-6 rounded-xl space-y-4 border border-gray-200"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
              <select
                value={paymentFilters.status}
                onChange={(e) => setPaymentFilters({...paymentFilters, status: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">Todos los estados</option>
                <option value="completed">Pagado</option>
                <option value="pending">Pendiente</option>
                <option value="overdue">Vencido</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Año/Período</label>
              <select
                value={paymentFilters.year}
                onChange={(e) => setPaymentFilters({...paymentFilters, year: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">Todos los años</option>
                {getUniqueYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Membresía</label>
              <select
                value={paymentFilters.membershipType}
                onChange={(e) => setPaymentFilters({...paymentFilters, membershipType: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">Todos los tipos</option>
                {getUniqueMembershipTypes().map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div className="relative member-search-container">
              <label className="block text-sm font-medium text-gray-700 mb-2">Socio Específico</label>
              <div className="relative">
                <input
                  type="text"
                  value={memberSearchText}
                  onChange={(e) => {
                    setMemberSearchText(e.target.value);
                    setShowMemberDropdown(true);
                  }}
                  onFocus={() => setShowMemberDropdown(true)}
                  placeholder="Buscar socio por nombre o RUT..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                {paymentFilters.memberId !== 'all' && (
                  <button
                    onClick={() => {
                      setPaymentFilters({...paymentFilters, memberId: 'all'});
                      setMemberSearchText('');
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                
                {showMemberDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div
                      onClick={() => {
                        setPaymentFilters({...paymentFilters, memberId: 'all'});
                        setMemberSearchText('');
                        setShowMemberDropdown(false);
                      }}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 font-medium text-gray-700"
                    >
                      Todos los socios
                    </div>
                    {members
                      .filter(member => {
                        if (!memberSearchText) return true;
                        const search = memberSearchText.toLowerCase();
                        const name = (member.name || '').toLowerCase();
                        const rut = (member.rut || '').toLowerCase();
                        return name.includes(search) || rut.includes(search);
                      })
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .slice(0, 50)
                      .map(member => (
                        <div
                          key={member.id}
                          onClick={() => {
                            setPaymentFilters({...paymentFilters, memberId: member.id});
                            setMemberSearchText(`${member.name} ${member.rut ? `(${member.rut})` : ''}`);
                            setShowMemberDropdown(false);
                          }}
                          className="px-4 py-2 hover:bg-green-50 cursor-pointer text-sm"
                        >
                          <div className="font-medium text-gray-900">{member.name}</div>
                          {member.rut && <div className="text-xs text-gray-500">{member.rut}</div>}
                        </div>
                      ))}
                    {members.filter(member => {
                      if (!memberSearchText) return true;
                      const search = memberSearchText.toLowerCase();
                      const name = (member.name || '').toLowerCase();
                      const rut = (member.rut || '').toLowerCase();
                      return name.includes(search) || rut.includes(search);
                    }).length === 0 && (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No se encontraron socios
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Monto Desde ($)</label>
              <input
                type="number"
                value={paymentFilters.amountFrom}
                onChange={(e) => setPaymentFilters({...paymentFilters, amountFrom: e.target.value})}
                placeholder="Mínimo"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Monto Hasta ($)</label>
              <input
                type="number"
                value={paymentFilters.amountTo}
                onChange={(e) => setPaymentFilters({...paymentFilters, amountTo: e.target.value})}
                placeholder="Máximo"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Desde</label>
              <input
                type="date"
                value={paymentFilters.dateFrom}
                onChange={(e) => setPaymentFilters({...paymentFilters, dateFrom: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Hasta</label>
              <input
                type="date"
                value={paymentFilters.dateTo}
                onChange={(e) => setPaymentFilters({...paymentFilters, dateTo: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>
          
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
          >
            <X className="w-4 h-4" />
            Limpiar Filtros
          </button>
        </motion.div>
      );
    } else if (reportType === 'donations') {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-gray-50 p-6 rounded-xl space-y-4 border border-gray-200"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Método de Pago</label>
              <select
                value={donationFilters.paymentMethod}
                onChange={(e) => setDonationFilters({...donationFilters, paymentMethod: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="all">Todos los métodos</option>
                <option value="wechat">WeChat Pay</option>
                <option value="alipay">Efectivo</option>
                <option value="bank">Transferencia Bancaria</option>
                <option value="cash">Efectivo</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar (Donante, Notas)</label>
              <input
                type="text"
                value={donationFilters.searchText}
                onChange={(e) => setDonationFilters({...donationFilters, searchText: e.target.value})}
                placeholder="Buscar..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Monto Desde ($)</label>
              <input
                type="number"
                value={donationFilters.amountFrom}
                onChange={(e) => setDonationFilters({...donationFilters, amountFrom: e.target.value})}
                placeholder="Mínimo"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Monto Hasta ($)</label>
              <input
                type="number"
                value={donationFilters.amountTo}
                onChange={(e) => setDonationFilters({...donationFilters, amountTo: e.target.value})}
                placeholder="Máximo"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Desde</label>
              <input
                type="date"
                value={donationFilters.dateFrom}
                onChange={(e) => setDonationFilters({...donationFilters, dateFrom: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Hasta</label>
              <input
                type="date"
                value={donationFilters.dateTo}
                onChange={(e) => setDonationFilters({...donationFilters, dateTo: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
          
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
          >
            <X className="w-4 h-4" />
            Limpiar Filtros
          </button>
        </motion.div>
      );
    }
  };

  // Renderizar contenido del reporte
  const renderReportContent = () => {
    if (reportType === 'members') {
      const filtered = getFilteredMembers();
      
      return (
        <div className="space-y-4">
          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Socios</p>
              <p className="text-2xl font-bold text-blue-600">{filtered.length}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Activos</p>
              <p className="text-2xl font-bold text-green-600">
                {filtered.filter(m => m.status === 'active').length}
              </p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Inactivos</p>
              <p className="text-2xl font-bold text-red-600">
                {filtered.filter(m => m.status === 'inactive').length}
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Pendientes</p>
              <p className="text-2xl font-bold text-yellow-600">
                {filtered.filter(m => m.status === 'pendiente').length}
              </p>
            </div>
          </div>
          
          {/* Tabla */}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow-sm">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nombre</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">RUT</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Teléfono</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Membresía</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Estado</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">F. Ingreso</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.length > 0 ? (
                  filtered.map(member => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{member.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{member.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{member.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{member.rut || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{member.phone || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{member.membershipType}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          member.status === 'active' ? 'bg-green-100 text-green-800' :
                          member.status === 'inactive' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {traducirEstado(member.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fechaCorta(member.joinDate)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      No se encontraron socios con los filtros aplicados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else if (reportType === 'payments') {
      const filtered = getFilteredPayments();
      const totalPagado = filtered.filter(p => p.status === 'completed').reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      const totalPendiente = filtered.filter(p => p.status === 'pending').reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      const totalVencido = filtered.filter(p => p.status === 'overdue').reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      
      return (
        <div className="space-y-4">
          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Pagos</p>
              <p className="text-2xl font-bold text-blue-600">{filtered.length}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Pagado</p>
              <p className="text-2xl font-bold text-green-600">${totalPagado.toLocaleString('es-ES')}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Pendiente</p>
              <p className="text-2xl font-bold text-yellow-600">${totalPendiente.toLocaleString('es-ES')}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Vencido</p>
              <p className="text-2xl font-bold text-red-600">${totalVencido.toLocaleString('es-ES')}</p>
            </div>
          </div>
          
          {/* Tabla */}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow-sm">
              <thead className="bg-green-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Socio</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Monto</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Estado</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">F. Pago</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Vencimiento</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Año</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Método</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.length > 0 ? (
                  filtered.map(payment => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{payment.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{payment.memberName}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-bold">${parseFloat(payment.amount).toLocaleString('es-ES')}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                          payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {traducirEstado(payment.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fechaCorta(payment.paymentDate)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fechaCorta(payment.dueDate)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{payment.membershipType || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{payment.año_periodo || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{traducirMetodo(payment.paymentMethod)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                      No se encontraron pagos con los filtros aplicados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else if (reportType === 'donations') {
      const filtered = getFilteredDonations();
      const totalDonado = filtered.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
      const avgDonation = filtered.length > 0 ? (totalDonado / filtered.length) : 0;
      
      return (
        <div className="space-y-4">
          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Donaciones</p>
              <p className="text-2xl font-bold text-purple-600">{filtered.length}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Monto Total</p>
              <p className="text-2xl font-bold text-green-600">${totalDonado.toLocaleString('es-ES')}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Promedio</p>
              <p className="text-2xl font-bold text-blue-600">${Math.round(avgDonation).toLocaleString('es-ES')}</p>
            </div>
          </div>
          
          {/* Tabla */}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow-sm">
              <thead className="bg-red-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Donante</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Monto</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Fecha</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Método</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Notas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.length > 0 ? (
                  filtered.map(donation => (
                    <tr key={donation.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{donation.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{donation.donorName}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-bold">${parseFloat(donation.amount).toLocaleString('es-ES')}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fechaCorta(donation.donationDate)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{traducirMetodo(donation.paymentMethod)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{donation.notes || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No se encontraron donaciones con los filtros aplicados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
            Centro de Reportes
          </h1>
          <p className="text-gray-600">Genere y visualice reportes detallados con filtros avanzados</p>
        </div>
        
        <div className="flex gap-3">
          <motion.button
            onClick={exportToCSV}
            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FileText className="w-5 h-5" />
            Exportar CSV
          </motion.button>
          
          <motion.button
            onClick={exportToPDF}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Download className="w-5 h-5" />
            Exportar PDF
          </motion.button>
        </div>
      </motion.div>

      {/* Selector de Tipo de Reporte */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Seleccionar Tipo de Reporte</h2>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
              showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-5 h-5" />
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
        </div>
        
        <div className="flex flex-wrap gap-4">
          <motion.button
            onClick={() => setReportType('members')}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${
              reportType === 'members' ? 'bg-blue-500 text-white shadow-lg scale-105' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Users className="w-5 h-5" />
            Socios
          </motion.button>
          
          <motion.button
            onClick={() => setReportType('payments')}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${
              reportType === 'payments' ? 'bg-green-500 text-white shadow-lg scale-105' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <CreditCard className="w-5 h-5" />
            Pagos de Membresía
          </motion.button>
          
          <motion.button
            onClick={() => setReportType('donations')}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${
              reportType === 'donations' ? 'bg-red-500 text-white shadow-lg scale-105' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Heart className="w-5 h-5" />
            Donaciones
          </motion.button>
        </div>
      </motion.div>

      {/* Filtros */}
      {renderFilters()}

      {/* Contenido del Reporte */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        key={reportType}
      >
        {renderReportContent()}
      </motion.div>
    </div>
  );
};

export default Reports;